import { apiClient } from './client';
import { ensureFreshToken } from '@/lib/session';
import type { components } from './schema.gen';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

/**
 * Direct re-exports of the wire types. The shape matches what the
 * backend returns from /v1/imports/** and /v1/imports/{id}.
 */
export type ImportRow = components['schemas']['ImportView'];
export type ImportDetail = components['schemas']['ImportDetailView'];
export type PreviewOutput = components['schemas']['PreviewOutputBody'];
export type PreviewRow = components['schemas']['PreviewRow'];

/**
 * Sources d'import acceptées par le serveur.
 *
 * PARTNER_BATCH est la source des fichiers dont le format n'est pas connu :
 * le téléversement est accepté, et c'est un profil de correspondance qui
 * fournit ensuite le connecteur. PARTNER en est l'ancien nom, conservé côté
 * serveur pour que les imports déjà enregistrés restent lisibles.
 */
export type ImportSource =
  | 'ORANGE'
  | 'ORANGE_PROFIL'
  | 'ORANGE_MENSUEL'
  | 'ID30'
  | 'CALL_CENTER'
  | 'PARTNER_BATCH'
  | 'PARTNER';

export type ImportFilters = {
  source?: ImportSource;
  status?:
    | 'PENDING'
    | 'RUNNING'
    | 'SUCCEEDED'
    | 'PARTIAL'
    | 'FAILED'
    | 'CANCELLED'
    | 'ROLLING_BACK'
    | 'ROLLED_BACK';
  limit?: number;
  offset?: number;
};

/**
 * POST /v1/imports — multipart upload. Bypasses openapi-fetch because
 * the typed client doesn't understand multipart bodies; we re-use the
 * Bearer from the auth store instead.
 */
/**
 * Le renouvellement préventif vit désormais dans `@/lib/session` : il est
 * partagé, et surtout unique.
 *
 * Il l'était déjà ici, mais pour lui seul — cette fonction lançait son propre
 * échange de jeton. Depuis que le serveur fait tourner le jeton de
 * renouvellement, deux échanges concurrents suffisent à ce que le second
 * présente un jeton déjà consommé : le serveur y voit un rejeu, et ferme la
 * session. Un import lancé pendant qu'une autre page renouvelait aurait
 * déconnecté l'utilisateur.
 *
 * La raison d'être reste la même : un téléversement passe par un `fetch` brut
 * — le client typé ne gère pas le multipart — et échappe au rattrapage sur 401.
 * Un jeton qui expire pendant l'envoi d'un fichier de 30 Mo se solderait par un
 * refus AVANT lecture du corps, et le navigateur ne rapporterait qu'une erreur
 * réseau opaque.
 */

export async function uploadImport(file: File, source: ImportSource): Promise<ImportDetail> {
  const token = await ensureFreshToken();
  const form = new FormData();
  form.append('source', source);
  form.append('file', file);
  const response = await fetch(`${BASE_URL}/v1/imports`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `upload failed: HTTP ${response.status}`);
  }
  return (await response.json()) as ImportDetail;
}

/**
 * GET /v1/imports/{id}/sheets — sheet names of an uploaded workbook. Returns
 * [] when the source has no selectable sheets (CSV, single-sheet). Raw fetch
 * because this endpoint isn't in the generated schema yet (regen needs a DB).
 */
export async function listImportSheets(id: number): Promise<string[]> {
  const token = await ensureFreshToken();
  const response = await fetch(`${BASE_URL}/v1/imports/${id}/sheets`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) return [];
  const body = (await response.json().catch(() => ({}))) as { sheets?: string[] };
  return body.sheets ?? [];
}

/**
 * GET /v1/imports/{id}/export-csv — download the chosen sheet of a stored xlsx
 * as CSV. Fetched with the Bearer (the endpoint is JWT-protected), then handed
 * to the browser as a file download via an object URL.
 */
export async function exportImportCsv(id: number, filename: string, sheet?: string): Promise<void> {
  const token = await ensureFreshToken();
  const qs = sheet ? `?sheet=${encodeURIComponent(sheet)}` : '';
  const response = await fetch(`${BASE_URL}/v1/imports/${id}/export-csv${qs}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new Error(`export CSV failed: HTTP ${response.status}`);
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

export async function previewImport(
  id: number,
  limit = 50,
  sheet?: string,
  mappingId?: number,
): Promise<PreviewOutput> {
  const query: Record<string, unknown> = { limit };
  if (sheet) query.sheet = sheet;
  if (mappingId) query.mappingId = mappingId;
  const { data, error } = await apiClient.POST('/v1/imports/{id}/preview', {
    params: { path: { id }, query: query as { limit: number } },
  });
  if (error || !data) {
    // Le serveur explique précisément ce qui bloque — colonne obligatoire
    // absente et colonnes réellement trouvées, feuille introuvable, profil
    // invalide. Avaler ce message et rendre `null` privait l'opérateur de la
    // seule information qui lui permet de corriger.
    const detail = (error as { detail?: string } | undefined)?.detail;
    throw new Error(detail ?? "aperçu impossible");
  }
  return data as PreviewOutput;
}

/**
 * POST /v1/imports/{id}/cancel — stop a PENDING or RUNNING import. Raw fetch
 * because the endpoint isn't in the generated schema yet (regen needs a DB).
 */
export async function cancelImport(id: number): Promise<ImportDetail | null> {
  const token = await ensureFreshToken();
  const response = await fetch(`${BASE_URL}/v1/imports/${id}/cancel`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) return null;
  return (await response.json()) as ImportDetail;
}

export type RollbackPlan = components['schemas']['ImportRollbackPlanView'];

/**
 * POST /v1/imports/{id}/rollback — retirer du journal tout ce qu'un lot a
 * écrit. Irréversible, réservé au rôle ADMIN. Le serveur répond 202 avec le
 * périmètre qu'il va supprimer, puis le lot passe ROLLING_BACK → ROLLED_BACK.
 *
 * Un refus (motif manquant, lot historique avec une autre activité de la même
 * source dans sa fenêtre…) remonte comme une erreur portant le message du
 * serveur : c'est ce texte que l'opérateur doit lire avant de forcer.
 */
export async function rollbackImport(
  id: number,
  input: { reason: string; force?: boolean; sourceSystem?: string; actorNames?: string[] },
): Promise<RollbackPlan> {
  const { data, error, response } = await apiClient.POST('/v1/imports/{id}/rollback', {
    params: { path: { id } },
    body: {
      reason: input.reason,
      force: input.force || undefined,
      sourceSystem: input.sourceSystem || undefined,
      actorNames: input.actorNames?.length ? input.actorNames : undefined,
    },
  });
  if (error || !data) {
    const e = error as { detail?: string; title?: string } | undefined;
    throw new Error(e?.detail ?? e?.title ?? `Retrait refusé (${response.status})`);
  }
  return data as RollbackPlan;
}

/** Origines possibles d'un recrutement, telles que l'API les accepte. */
export type RecruitmentOrigin = 'MAFA_TERRAIN' | 'ORANGE_PARTENAIRE' | 'ID30';

export const RECRUITMENT_ORIGINS: { value: RecruitmentOrigin; label: string }[] = [
  { value: 'ORANGE_PARTENAIRE', label: 'Orange ou un partenaire' },
  { value: 'MAFA_TERRAIN', label: 'Nos équipes terrain' },
  { value: 'ID30', label: 'PDE.CI' },
];

export async function runImport(
  id: number,
  sheet?: string,
  origin?: { recruitmentOrigin?: RecruitmentOrigin; recruitmentPartner?: string },
): Promise<ImportDetail | null> {
  const params: { path: { id: number }; query?: Record<string, unknown> } = { path: { id } };
  if (sheet) params.query = { sheet };
  const { data, error } = await apiClient.POST('/v1/imports/{id}/run', {
    params: params as { path: { id: number } },
    body: {
      recruitmentOrigin: origin?.recruitmentOrigin,
      recruitmentPartner: origin?.recruitmentPartner,
    },
  });
  if (error) return null;
  return data as ImportDetail;
}

export async function listImports(filters: ImportFilters = {}): Promise<{
  items: ImportRow[];
  total: number;
}> {
  const { data, error } = await apiClient.GET('/v1/imports', {
    params: {
      query: {
        source: filters.source ?? '',
        status: filters.status ?? '',
        limit: filters.limit ?? 50,
        offset: filters.offset ?? 0,
      },
    },
  });
  if (error || !data) return { items: [], total: 0 };
  return { items: (data.items ?? []) as ImportRow[], total: data.total ?? 0 };
}

export async function getImport(id: number): Promise<ImportDetail | null> {
  const { data, error } = await apiClient.GET('/v1/imports/{id}', {
    params: { path: { id } },
  });
  if (error || !data) return null;
  return data as ImportDetail;
}
